import { useTranslation } from 'react-i18next';
import { BADEN_WUERTTEMBERG_2026, HOLIDAY_SAMPLE_MONTHS, HOLIDAY_SAMPLE_YEAR, type CalendarDayKind } from '../../../data/holidays';
import styles from './HolidayCalendar.module.css';

type DayKind = CalendarDayKind | 'weekend' | 'workday';

interface CalendarCell {
    day: number;
    kind: DayKind;
}

const DAYS_PER_WEEK = 7;
const SATURDAY_INDEX = 5;
const markedDays = new Map(BADEN_WUERTTEMBERG_2026.map((entry) => [entry.date, entry.kind]));

function isoDate(year: number, month: number, day: number): string {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function monthCells(year: number, month: number): Array<CalendarCell | null> {
    const firstWeekday = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
    const dayCount = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const leading: null[] = Array.from({ length: firstWeekday }, () => null);
    const days = Array.from({ length: dayCount }, (_, index): CalendarCell => {
        const day = index + 1;
        const weekday = (firstWeekday + index) % DAYS_PER_WEEK;
        const marked = markedDays.get(isoDate(year, month, day));
        return { day, kind: marked ?? (weekday >= SATURDAY_INDEX ? 'weekend' : 'workday') };
    });
    return [...leading, ...days];
}

export default function HolidayCalendar() {
    const { t } = useTranslation();
    const monthNames = t('calendar.months', { returnObjects: true }) as unknown as string[];
    const weekdayNames = t('calendar.weekdays', { returnObjects: true }) as unknown as string[];

    return (
        <figure className={styles.calendar}>
            <div className={styles.months}>
                {HOLIDAY_SAMPLE_MONTHS.map((month) => (
                    <div key={month} className={styles.month}>
                        <p className={styles.monthName}>{monthNames[month - 1]} {HOLIDAY_SAMPLE_YEAR}</p>
                        <div className={styles.grid} role="presentation">
                            {weekdayNames.map((weekday) => (
                                <span key={weekday} className={styles.weekday}>{weekday}</span>
                            ))}
                            {monthCells(HOLIDAY_SAMPLE_YEAR, month).map((cell, index) =>
                                cell ? (
                                    <span key={index} className={styles.day} data-kind={cell.kind}>{cell.day}</span>
                                ) : (
                                    <span key={index} aria-hidden="true" />
                                )
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <figcaption className={styles.legend}>
                <span className={styles.legendItem} data-kind="holiday">{t('calendar.holiday')}</span>
                <span className={styles.legendItem} data-kind="bridge">{t('calendar.bridge')}</span>
                <span className={styles.region}>{t('calendar.region')}</span>
            </figcaption>
        </figure>
    );
}
